
import React, { useMemo } from 'react';
import { Expense, Category, Group, User, ExpenseStatus } from '../types';
import { Bell, AlertTriangle, TrendingUp, Users, Clock, CheckCircle2 } from 'lucide-react';

interface NotificationsProps {
  expenses: Expense[]; // Projected monthly expenses
  categories: Category[];
  groups: Group[];
  currentUser: User;
  allUsers: User[];
  selectedDate: Date;
}

type NotificationType = 'warning' | 'info' | 'success' | 'group' | 'bill';

interface NotificationItem {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  date?: string; // ISO or display date
  actionLabel?: string;
  priority: number; // Higher is more important
}

export const Notifications: React.FC<NotificationsProps> = ({ expenses, categories, groups, currentUser, allUsers, selectedDate }) => {

  const notifications = useMemo(() => {
    const alerts: NotificationItem[] = [];
    const currentMonthExpenses = expenses.filter(e => e.type === 'expense');

    // 1. Global Budget Alerts
    const totalSpent = currentMonthExpenses.reduce((acc, cur) => acc + cur.amount, 0);
    const budgetLimit = currentUser.monthlyBudget;
    
    if (budgetLimit > 0) {
      const percentage = (totalSpent / budgetLimit) * 100;
      if (percentage >= 100) {
        alerts.push({
          id: 'budget-exceeded',
          type: 'warning',
          title: 'Orçamento Excedido',
          message: `Você gastou R$ ${totalSpent.toFixed(2)}, ultrapassando sua meta de R$ ${budgetLimit.toFixed(2)}.`,
          priority: 10
        });
      } else if (percentage >= 80) {
        alerts.push({
          id: 'budget-warning',
          type: 'info',
          title: 'Atenção ao Orçamento',
          message: `Você já consumiu ${percentage.toFixed(0)}% da sua meta mensal.`,
          priority: 5
        });
      }
    }

    // 2. Category Alerts
    categories.forEach(cat => {
      if (cat.alertEnabled && cat.budgetLimit > 0) {
        const catSpent = currentMonthExpenses
          .filter(e => e.categoryId === cat.id)
          .reduce((acc, cur) => acc + cur.amount, 0);
        
        const catPct = (catSpent / cat.budgetLimit) * 100;

        if (catPct >= 100) {
          alerts.push({
            id: `cat-exceeded-${cat.id}`,
            type: 'warning',
            title: `Limite de ${cat.name}`,
            message: `Você estourou o limite definido para ${cat.name}.`,
            priority: 8
          });
        } else if (catPct >= 80) {
           alerts.push({
            id: `cat-warning-${cat.id}`,
            type: 'info',
            title: `${cat.name} em Alerta`,
            message: `Você atingiu ${catPct.toFixed(0)}% do limite de ${cat.name}.`,
            priority: 4
          });
        }
      }
    });

    // 3. Pending/Late Bills
    const pendingBills = expenses.filter(e => 
      e.type === 'expense' && 
      (e.status === ExpenseStatus.PENDING || e.status === ExpenseStatus.LATE)
    );

    pendingBills.forEach(bill => {
       const dueDate = new Date(bill.date);
       const today = new Date();
       const isLate = dueDate < today;
       
       alerts.push({
         id: `bill-${bill.id}`,
         type: 'bill',
         title: isLate ? 'Conta Atrasada' : 'Conta Pendente',
         message: `${bill.description} - R$ ${bill.amount.toFixed(2)} vence ${isLate ? 'em' : 'dia'} ${dueDate.toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit'})}`,
         priority: isLate ? 9 : 6
       });
    });

    // 4. Group Activity (Simulated "New" activity based on current month)
    // We show expenses created by OTHERS in the current view
    const groupActivity = expenses.filter(e => 
      e.groupId && 
      e.paidByUserId !== currentUser.id
    );

    groupActivity.forEach(activity => {
      const group = groups.find(g => g.id === activity.groupId);
      const user = allUsers.find(u => u.id === activity.paidByUserId);
      
      if (group && user) {
        alerts.push({
          id: `group-act-${activity.id}`,
          type: 'group',
          title: `Nova despesa em ${group.name}`,
          message: `${user.name.split(' ')[0]} adicionou "${activity.description}" (R$ ${activity.amount.toFixed(2)}).`,
          priority: 3,
          date: activity.date
        });
      }
    });

    // Sort by priority desc, then date desc
    return alerts.sort((a, b) => b.priority - a.priority);
  }, [expenses, categories, currentUser, groups, allUsers]);

  const getIcon = (type: NotificationType) => {
    switch(type) {
      case 'warning': return <AlertTriangle className="text-red-500 dark:text-red-400" />;
      case 'info': return <TrendingUp className="text-amber-500 dark:text-amber-400" />;
      case 'bill': return <Clock className="text-slate-700 dark:text-slate-300" />;
      case 'group': return <Users className="text-blue-500 dark:text-blue-400" />;
      default: return <Bell className="text-slate-500" />;
    }
  };

  const getBgColor = (type: NotificationType) => {
    switch(type) {
      case 'warning': return 'bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20';
      case 'info': return 'bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20';
      case 'bill': return 'bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800';
      case 'group': return 'bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20';
      default: return 'bg-slate-50 dark:bg-zinc-800';
    }
  };

  return (
    <div className="space-y-6 pb-20">
       <div>
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Notificações</h2>
          <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">Alertas e atividades recentes</p>
       </div>

       {notifications.length === 0 ? (
         <div className="text-center py-20 bg-white dark:bg-zinc-900 rounded-[2rem] border border-dashed border-slate-200 dark:border-zinc-800">
            <div className="w-16 h-16 bg-slate-50 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300 dark:text-slate-600">
               <CheckCircle2 size={32} />
            </div>
            <p className="text-slate-500 dark:text-slate-400 font-bold">Tudo tranquilo por aqui</p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Você não tem novos alertas.</p>
         </div>
       ) : (
         <div className="space-y-3">
           {notifications.map(item => (
             <div 
               key={item.id} 
               className={`p-5 rounded-[1.5rem] flex gap-4 items-start transition-transform active:scale-[0.99] ${getBgColor(item.type)}`}
             >
                <div className={`p-3 rounded-xl bg-white dark:bg-zinc-800 shadow-sm shrink-0`}>
                   {getIcon(item.type)}
                </div>
                <div className="flex-1 min-w-0">
                   <div className="flex justify-between items-start">
                      <h4 className="font-bold text-slate-800 dark:text-slate-200 text-sm truncate pr-2">{item.title}</h4>
                      {item.date && (
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium whitespace-nowrap">
                          {new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                        </span>
                      )}
                   </div>
                   <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 leading-relaxed">{item.message}</p>
                   
                   {item.type === 'bill' && (
                     <div className="mt-3">
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-slate-50 dark:bg-zinc-950 px-2 py-1 rounded-md text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-zinc-800">
                          Pendente
                        </span>
                     </div>
                   )}
                </div>
             </div>
           ))}
         </div>
       )}
    </div>
  );
};
